/* eslint-disable no-console */
import {SearchOutlined} from '@ant-design/icons';
import {Button, Checkbox, Col, Collapse, Flex, Form, Input, Row} from 'antd';
import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import MainTableTitle from '../../../../../components/Table/Utils/MainTableTitle';
import {CommonErrors} from '../../../../../constants/Common';
import {allRoutes} from '../../../../../constants/Routes';
import {APITypeEnum} from '../../../../../services/interface/tableAPIType';
import addRole from '../../../../../services/modules/UserManager/addRole';
import useCompanyStore from '../../../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../../../store/userStore/userStore';
import {FirstCap} from '../../../../../utils/FormUtils/FirstCap';
import RegEx from '../../../../../utils/FormUtils/RegEx';
import showMessage from '../../../../../utils/showMessage';
import {searchItems} from '../../../Constants/commonFunctions';
import {NewPermissionDataType} from '../Constants/addRoleInterface';
import transformPermissions from '../Constants/arrayTransform';

const {Panel} = Collapse;

const AddRole: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [editId, setEditId] = useState('');
  const permissionsAPI = useUserStore.getState().permission;
  const [formChanged, setFormChanged] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchText, setSearchText] = useState('');
  const companyID = useCompanyStore.getState().companyId;
  const user = useUserStore.getState().userDetails;
  // const userPer = useUserStore.getState().userPermission;

  const [items, setItems] = useState<NewPermissionDataType[]>(
    transformPermissions(permissionsAPI)
  );

  useEffect(() => {
    if (!!location.state) {
      setEditId(location.state._id);
      form.setFieldsValue(location.state);

      const permissionsToEdit = location.state.permissions || [];
      const updatedItems = items.map((item) => {
        return {
          ...item,
          all: item.subTab.every((subTabItem) =>
            subTabItem.allOptions.every((option) =>
              permissionsToEdit.includes(option.key)
            )
          ),
          subTab: item.subTab.map((subtabItem) => ({
            ...subtabItem,
            allOptions: subtabItem.allOptions.map((option) => ({
              ...option,
              checked: permissionsToEdit.includes(option.key)
            }))
          }))
        };
      });
      setItems(updatedItems);
    } else {
      form.resetFields();
    }
  }, [location.state]);

  const handleSubmit = (submitData: any) => {
    const checkedPermissions = items.flatMap((item) =>
      item.subTab.flatMap((subtab) =>
        subtab.allOptions
          .filter((option) => option.checked)
          .map((option) => option.key)
      )
    );

    addRole(
      editId ? APITypeEnum.PUT : APITypeEnum.POST_NEW,
      {
        companyId: companyID,
        title: submitData.title,
        permissions: checkedPermissions,
        createdBy: editId ? submitData.createdBy : user._id
      },
      editId
    )
      .then((res) => {
        showMessage(res);
        if (res?.status !== 'error') {
          navigate(allRoutes.setting.roleManager);
        }
      })
      .catch(() => {})
      .finally(() => {});
  };

  const handleSelectAllChange = (checked: boolean) => {
    const updatedItems = items.map((item) => ({
      ...item,
      all: item.order === 1 ? true : checked,
      subTab: item.subTab.map((subtab) => ({
        ...subtab,
        allOptions: subtab.allOptions.map((option) => ({
          ...option,
          checked: item.order === 1 ? true : checked
        }))
      }))
    }));
    setItems(updatedItems);
  };

  const handleAllChange = (itemKey: string, checked: boolean) => {
    const updatedItems = items.map((item) => {
      if (item.key === itemKey) {
        const updatedSubTab = item.subTab.map((subtabItem) => ({
          ...subtabItem,
          allOptions: subtabItem.allOptions.map((option) => ({
            ...option,
            checked
          }))
        }));

        return {
          ...item,
          all: checked,
          subTab: updatedSubTab
        };
      } else {
        return item;
      }
    });

    setItems(updatedItems);
  };

  const handleOptionChange = (
    itemIndex: number,
    subTabIndex: number,
    optionIndex: number,
    checked: boolean
  ) => {
    const updatedItems = [...items];

    if (
      itemIndex >= 0 &&
      itemIndex < updatedItems.length &&
      subTabIndex >= 0 &&
      subTabIndex < updatedItems[itemIndex].subTab.length &&
      optionIndex >= 0 &&
      optionIndex <
        updatedItems[itemIndex].subTab[subTabIndex].allOptions.length
    ) {
      updatedItems[itemIndex].subTab[subTabIndex].allOptions[
        optionIndex
      ].checked = checked;

      const allOptionsChecked = updatedItems[itemIndex].subTab[
        subTabIndex
      ].allOptions.every((option) => option.checked);

      updatedItems[itemIndex].all = allOptionsChecked;

      const isPanelExpanded = expandedKeys.includes(
        updatedItems[itemIndex].key
      );

      if (!isPanelExpanded && checked) {
        setExpandedKeys((prevExpandedKeys) => [
          ...prevExpandedKeys,
          updatedItems[itemIndex].key
        ]);
      }

      setItems(updatedItems);
    }
  };

  const onExpandAll = () => {
    if (expandedKeys.length === 0) {
      setExpandedKeys(
        items.map((item) => {
          return item.key;
        })
      );
    } else {
      setExpandedKeys([]);
    }
  };

  const searchHandler = (selectedTerm: any) => {
    setSearchText(selectedTerm);
  };
  const onPanelChange = (newKeys: string | string[]) => {
    setExpandedKeys(newKeys as string[]);
  };

  return (
    <>
      <Flex className="userFlex">
        <MainTableTitle title={`${editId ? 'Update' : 'Add'} Role`} />
        <Flex className="content">
          <Form
            form={form}
            onFinish={handleSubmit}
            style={{width: '100%'}}
            onValuesChange={() => {
              setFormChanged(true);
            }}
            // disabled={!userPer.setting.userManager.shouldEdit}
          >
            <Row gutter={{xs: 6, sm: 12, md: 18, lg: 24}}>
              <Col span={6}>
                <Form.Item
                  name="title"
                  rules={[
                    {
                      required: true,
                      message: CommonErrors.mandatory('Role Name')
                    },
                    {
                      max: 100,
                      message: CommonErrors.notMoreThan('Role Name', 100)
                    },
                    {
                      min: 2,
                      message: CommonErrors.notLessThan('Role Name', 1)
                    },
                    {
                      pattern: RegEx.roleName,
                      message: 'No special characters are Allowed'
                    },
                    {
                      whitespace: true,
                      message: CommonErrors.mandatory('Role Name')
                    }
                  ]}
                  label="Role Name"
                  labelCol={{span: 24}}
                  tooltip="This is the name of the role"
                >
                  <Input
                    placeholder="Role Name"
                    className="inputRole"
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.value = FirstCap(target.value);
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <div className="titleText">Pages Permissions</div>
            <div className="title2">Manage Permissions</div>
            <Input
              allowClear
              className="searchBar"
              value={searchText}
              onChange={(event) => {
                searchHandler(event.target.value);
              }}
              addonBefore={
                <div
                  className="searchBarButton"
                  onClick={() => {
                    searchHandler(searchText);
                  }}
                >
                  <SearchOutlined
                    width={15}
                    height={15}
                    style={{color: '#AAAAAA'}}
                  />
                </div>
              }
              placeholder="Search"
            />
            <div className="selectAllPermissions">
              <div>
                <Checkbox
                  checked={items.every((item) =>
                    item.subTab.every((subTab) =>
                      subTab.allOptions.every((option) => option.checked)
                    )
                  )}
                  indeterminate={
                    items.some((item) =>
                      item.subTab.some((subTab) =>
                        subTab.allOptions.some((option) => option.checked)
                      )
                    ) &&
                    !items.every((item) =>
                      item.subTab.every((subTab) =>
                        subTab.allOptions.every((option) => option.checked)
                      )
                    )
                  }
                  onChange={(e) => {
                    handleSelectAllChange(e.target.checked);
                    setFormChanged(true);
                  }}
                >
                  <div className="selectAllTitle">Select All Permissions</div>
                </Checkbox>
              </div>
              <div className="expandAll" onClick={onExpandAll}>
                {expandedKeys.length === 0 ? 'Expand All' : 'Collapse All'}
              </div>
            </div>

            <Collapse
              expandIconPosition="end"
              className="collapse"
              activeKey={expandedKeys}
              collapsible="icon"
              onChange={onPanelChange}
            >
              {items
                .filter((item) => {
                  if (searchText) {
                    return searchItems(searchText, item);
                  } else {
                    return true;
                  }
                })
                .map((filteredItem, filteredItemIndex) => {
                  const filterEvery = filteredItem.subTab.every((subTabItem) =>
                    subTabItem.allOptions.every((option) => option.checked)
                  );

                  const selected = filteredItem?.subTab?.filter((item) => {
                    return item?.allOptions.some((option) => {
                      return option?.checked;
                    });
                  });

                  return (
                    <Panel
                      header={
                        <Checkbox
                          checked={filterEvery}
                          indeterminate={
                            filteredItem.subTab.some((subTabItem) =>
                              subTabItem.allOptions.some(
                                (option) => option.checked
                              )
                            ) && !filterEvery
                          }
                          disabled={filteredItem.order === 1}
                          onChange={(e) => {
                            handleAllChange(filteredItem.key, e.target.checked);
                            setFormChanged(true);
                          }}
                        >
                          <div className="panelHeader">
                            {filteredItem.title}
                          </div>
                        </Checkbox>
                      }
                      collapsible="icon"
                      key={filteredItem.key}
                      extra={
                        <div
                          style={{
                            font: 'normal normal 500 14px/25px Montserrat',
                            color: '#677489',
                            letterSpacing: '0.44px'
                          }}
                        >{`${selected?.length}/${filteredItem?.subTab?.length}`}</div>
                      }
                    >
                      {filteredItem.subTab.map((subTabItem, subTabIndex) => {
                        return (
                          <div
                            className="allCheckboxes"
                            key={subTabItem.subTabLabel}
                          >
                            <div className="allOptionsCheckbox">
                              <div className="subTabLabelTitle">
                                {subTabItem.subTabLabel}
                              </div>
                              {subTabItem.allOptions.map(
                                (option, optionIndex) => {
                                  return (
                                    <div
                                      key={option.key}
                                      className="allOptionsDiv"
                                    >
                                      <Checkbox
                                        checked={option.checked}
                                        onChange={(e) => {
                                          handleOptionChange(
                                            filteredItemIndex,
                                            subTabIndex,
                                            optionIndex,
                                            e.target.checked
                                          );
                                          setFormChanged(true);
                                        }}
                                        className="optionsCheckbox"
                                        disabled={filteredItem.order === 1}
                                      >
                                        <div className="optionCheckboxtitle">
                                          {option.type
                                            ? FirstCap(option.type)
                                            : subTabItem.subTabLabel}
                                        </div>
                                      </Checkbox>
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </Panel>
                  );
                })}
            </Collapse>
            <Form.Item>
              <Button
                className="buttonSave"
                type="primary"
                htmlType="submit"
                disabled={!formChanged}
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        </Flex>
      </Flex>
    </>
  );
};

export default AddRole;
